import React, { ReactElement, useState } from 'react'
import styled from 'styled-components'
import {
  Box,
  Button,
  ButtonGroup,
  CheckboxGroup,
  CheckboxStateful,
  Space,
  TextBody,
} from '@ally/metronome-ui'

import { WidgetKey } from '../types'
import { useCustomizeDashboard } from '../CustomizeDashboardContext/CustomizeDashboardContext'
import { getDisabledWidgets } from '../utils'

const StyledAddWidgetsContainer = styled(Box)`
  ${({ theme: { colors, space } }): string =>
    `
    box-shadow: 0 2px 4px -2px rgba(0, 0, 0, 0.16), 0 5px 8px -4px rgba(0, 0, 0, 0.16);
    border: 1px solid ${colors['gray-300']};
    border-radius: 4px;
    margin-bottom: ${space.lg};
    padding: ${space.md} ${space.sm};
  `};
`

const StyledCheckboxStateful = styled(CheckboxStateful)`
  align-items: flex-start;
`

const CheckboxLabel: React.FC<{ widget: WidgetKey }> = ({
  widget,
}): ReactElement => {
  const [{ eligibleWidgets }] = useCustomizeDashboard()
  const { displayName, description } = eligibleWidgets[widget]?.copy ?? {}
  return (
    <Box pt="2.5px">
      <TextBody tag="p" size="sm" weight="bold">
        {displayName}:
      </TextBody>
      <TextBody tag="span" size="sm">
        {description}
      </TextBody>
    </Box>
  )
}

const CheckboxItem: React.FC<{
  widget: WidgetKey
  onChange: (e: React.BaseSyntheticEvent) => void
}> = ({ widget, onChange }): ReactElement => {
  return (
    <Space my="sm" key={widget}>
      <StyledCheckboxStateful
        label={<CheckboxLabel widget={widget} />}
        name={widget as string}
        onChange={onChange}
        allytmfn={`add checkbox ${widget}`}
      />
    </Space>
  )
}

const StyledCheckboxGroup = styled(CheckboxGroup)`
  padding-left: 0;
  margin-left: 0;
`

const AddWidgets: React.FC<{ cancelAddWidgets: () => void }> = ({
  cancelAddWidgets,
}) => {
  const [
    { widgets, isSaving, customizeDataToPatch },
    dispatch,
  ] = useCustomizeDashboard()

  const currentWidgets = customizeDataToPatch.widgets ?? widgets

  const [widgetData, setWidgetData] = useState({
    uninstalledWidgets: getDisabledWidgets(currentWidgets).map(
      w => w.name,
    ) as WidgetKey[],
    toAdd: [] as WidgetKey[],
  })

  function addWidgets(): void {
    widgetData.toAdd.forEach(widget => {
      ;(window as any).allytm.event('clickEvent', {
        tagName: 'BUTTON',
        allytmln: `addWidget-${widget}`,
      })
    })

    dispatch({
      type: 'Add Widgets',
      payload: widgetData.toAdd,
    })

    cancelAddWidgets()
  }

  function updateWidgetsToAdd(e: React.BaseSyntheticEvent): void {
    setWidgetData({
      ...widgetData,
      toAdd: e.target.checked
        ? [...widgetData.toAdd, e.target.name]
        : widgetData.toAdd.filter(widget => widget !== e.target.name),
    })
  }
  return (
    <>
      <StyledAddWidgetsContainer>
        <Box pb="sm" borderBottom="1px solid" borderColor="slate-1">
          <TextBody tag="p" size="sm" weight="bold">
            + Add Widgets
          </TextBody>
        </Box>
        <StyledCheckboxGroup
          name="Add Widgets"
          legendText="Add Widgets"
          isSROnly
        >
          {widgetData.uninstalledWidgets.map(widget => (
            <CheckboxItem
              widget={widget}
              onChange={updateWidgetsToAdd}
              key={`add-${widget}`}
            />
          ))}
        </StyledCheckboxGroup>
        <ButtonGroup>
          <Button
            variant="secondary"
            text="Add"
            onClick={(): void => addWidgets()}
            allytmln="Add Widgets button"
            disabled={isSaving}
            aria-disabled={isSaving}
          />
          <Button
            variant="link"
            text="Cancel"
            onClick={(): void => cancelAddWidgets()}
            allytmln="Cancel Add Widgets button"
            disabled={isSaving}
            aria-disabled={isSaving}
          />
        </ButtonGroup>
      </StyledAddWidgetsContainer>
    </>
  )
}

export default AddWidgets
